import { zodResolver } from "@hookform/resolvers/zod";
import { EventTypeCustomInputType, WorkflowActions } from "@prisma/client";
import { useMutation } from "@tanstack/react-query";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useSession } from "next-auth/react";
import { Trans } from "next-i18next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo, useReducer, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { FormattedNumber, IntlProvider } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import BookingPageTagManager from "@calcom/app-store/BookingPageTagManager";
import {
  EventLocationType,
  getEventLocationType,
  getEventLocationValue,
  getHumanReadableLocationValue,
  locationKeyToString,
} from "@calcom/app-store/locations";
import { createPaymentLink } from "@calcom/app-store/stripepayment/lib/client";
import { getEventTypeAppData } from "@calcom/app-store/utils";
import { LocationObject, LocationType } from "@calcom/core/location";
import dayjs from "@calcom/dayjs";
import {
  useEmbedNonStylesConfig,
  useEmbedUiConfig,
  useIsBackgroundTransparent,
  useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import CustomBranding from "@calcom/lib/CustomBranding";
import classNames from "@calcom/lib/classNames";
import { APP_NAME, WEBAPP_URL } from "@calcom/lib/constants";
import getPaymentAppData from "@calcom/lib/getPaymentAppData";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import useTheme from "@calcom/lib/hooks/useTheme";
import { HttpError } from "@calcom/lib/http-error";
import { getEveryFreqFor } from "@calcom/lib/recurringStrings";
import slugify from "@calcom/lib/slugify";
import { collectPageParameters, telemetryEventTypes } from "@calcom/lib/telemetry";
import { useTelemetry } from "@calcom/lib/telemetry-client";
import {
  AddressInput,
  Button,
  EmailField,
  EmailInput,
  Form,
  Group,
  PhoneInput,
  RadioField,
  Tooltip,
  Checkbox,
} from "@calcom/ui";
import {
  FiUserPlus,
  FiCalendar,
  FiX,
  FiInfo,
  FiCreditCard,
  FiRefreshCw,
  FiUser,
  FiAlertTriangle,
} from "@calcom/ui/components/icon";

import { asStringOrNull } from "@lib/asStringOrNull";
import { timeZone } from "@lib/clock";
import { ensureArray } from "@lib/ensureArray";
import useRouterQuery from "@lib/hooks/useRouterQuery";
import createBooking from "@lib/mutations/bookings/create-booking";
import createRecurringBooking from "@lib/mutations/bookings/create-recurring-booking";
import { parseDate, parseRecurringDates } from "@lib/parseDate";

import Gates, { Gate, GateState } from "@components/Gates";
import BookingDescription from "@components/booking/BookingDescription";

import { BookPageProps } from "../../../pages/[user]/book";
import { HashLinkPageProps } from "../../../pages/d/[link]/book";
import { TeamBookingPageProps } from "../../../pages/team/[slug]/book";

type BookingPageProps = (BookPageProps | TeamBookingPageProps | HashLinkPageProps) & {
  handleBookingConfirmation?: (
    uid: string,
    email: string,
    eventTypeSlug: string,
    rescheduleUid: string,
    bookingStartTime: Date | undefined
  ) => void;
  handleCancel?: () => void;
};

type BookingFormValues = {
  name: string;
  email: string;
  notes?: string;
  locationType?: EventLocationType["type"];
  guests?: { email: string }[];
  address?: string;
  attendeeAddress?: string;
  phone?: string;
  hostPhoneNumber?: string; // Maybe come up with a better way to name this to distingish between two types of phone numbers
  customInputs?: {
    [key: string]: string | boolean;
  };
  rescheduleReason?: string;
  smsReminderNumber?: string;
  tosAgreement?: boolean;
};

const BookingPage = ({
  eventType,
  booking,
  profile,
  isDynamicGroupBooking,
  recurringEventCount,
  hasHashedBookingLink,
  hashedLink,
  handleBookingConfirmation,
  handleCancel,
  ...restProps
}: BookingPageProps) => {
  const { t, i18n } = useLocale();
  const { duration: queryDuration } = useRouterQuery("duration");
  const isEmbed = useIsEmbed(restProps.isEmbed);
  const embedUiConfig = useEmbedUiConfig();
  const shouldAlignCentrallyInEmbed = useEmbedNonStylesConfig("align") !== "left";
  const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;
  const router = useRouter();
  const { data: session, status: sessionStatus } = useSession();
  const isBackgroundTransparent = useIsBackgroundTransparent();
  const telemetry = useTelemetry();
  const [gateState, gateDispatcher] = useReducer(
    (state: GateState, newState: Partial<GateState>) => ({
      ...state,
      ...newState,
    }),
    {}
  );
  const paymentAppData = getPaymentAppData(eventType);
  // Define duration now that we support multiple duration eventTypes
  let duration = eventType.length;
  if (
    queryDuration &&
    !isNaN(Number(queryDuration)) &&
    eventType.metadata?.multipleDuration &&
    eventType.metadata?.multipleDuration.includes(Number(queryDuration))
  ) {
    duration = Number(queryDuration);
  }

  useEffect(() => {
    if (top !== window) {
      //page_view will be collected automatically by _middleware.ts
      telemetry.event(
        telemetryEventTypes.embedView,
        collectPageParameters("/book", { isTeamBooking: document.URL.includes("team/") })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutation = useMutation(createBooking, {
    onSuccess: async (responseData) => {
      const { uid, paymentUid } = responseData;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).dataLayer.push({ event: "booking_confirm" });
      } catch (e) {
        console.error("dataLayer.push error", e);
      }

      if (paymentUid) {
        return await router.push(
          createPaymentLink({
            paymentUid,
            date,
            name: bookingForm.getValues("name"),
            email: bookingForm.getValues("email"),
            absolute: false,
          })
        );
      }
      if (handleBookingConfirmation) {
        handleBookingConfirmation(
          uid,
          bookingForm.getValues("email"),
          eventType.slug,
          rescheduleUid,
          booking?.startTime
        );
        return;
      }

      return router.push({
        pathname: `/booking/${uid}`,
        query: {
          isSuccessBookingPage: true,
          email: bookingForm.getValues("email"),
          eventTypeSlug: eventType.slug,
          ...(rescheduleUid && booking?.startTime && { formerTime: booking.startTime.toString() }),
        },
      });
    },
  });

  const recurringMutation = useMutation(createRecurringBooking, {
    onSuccess: async (responseData = []) => {
      const { uid } = responseData[0] || {};

      return router.push({
        pathname: `/booking/${uid}`,
        query: {
          allRemainingBookings: true,
          email: bookingForm.getValues("email"),
          eventTypeSlug: eventType.slug,
          formerTime: booking?.startTime.toString(),
        },
      });
    },
  });

  const rescheduleUid = router.query.rescheduleUid as string;
  useTheme(profile.theme);
  const date = asStringOrNull(router.query.date);

  const [guestToggle, setGuestToggle] = useState(booking && booking.attendees.length > 1);
  // it would be nice if Prisma at some point in the future allowed for Json<Location>; as of now this is not the case.
  const locations: LocationObject[] = useMemo(
    () => (eventType.locations as LocationObject[]) || [],
    [eventType.locations]
  );

  useEffect(() => {
    if (router.query.guest) {
      setGuestToggle(true);
    }
  }, [router.query.guest]);

  const loggedInIsOwner = eventType?.users[0]?.id === session?.user?.id;
  const guestListEmails = !isDynamicGroupBooking
    ? booking?.attendees.slice(1).map((attendee) => {
        return { email: attendee.email };
      })
    : [];

  // There should only exists one default userData variable for primaryAttendee.
  const defaultUserValues = {
    email: rescheduleUid
      ? booking?.attendees[0].email
      : router.query.email
      ? (router.query.email as string)
      : "",
    name: rescheduleUid ? booking?.attendees[0].name : router.query.name ? (router.query.name as string) : "",
  };

  const defaultValues = () => {
    if (!rescheduleUid) {
      return {
        name: defaultUserValues.name || (!loggedInIsOwner && session?.user?.name) || "",
        email: defaultUserValues.email || (!loggedInIsOwner && session?.user?.email) || "",
        notes: (router.query.notes as string) || "",
        guests: ensureArray(router.query.guest).map((guest) => {
          return { email: guest as string };
        }),
        customInputs: eventType.customInputs.reduce(
          (customInputs, input) => ({
            ...customInputs,
            [input.id]: router.query[slugify(input.label)],
          }),
          {}
        ),
      };
    }
    if (!booking || !booking.attendees.length) {
      return {};
    }
    const primaryAttendee = booking.attendees[0];
    if (!primaryAttendee) {
      return {};
    }

    const customInputType = booking.customInputs;
    return {
      name: defaultUserValues.name,
      email: defaultUserValues.email || "",
      guests: guestListEmails,
      notes: booking.description || "",
      rescheduleReason: "",
      smsReminderNumber: booking.smsReminderNumber || undefined,
      customInputs: eventType.customInputs.reduce(
        (customInputs, input) => ({
          ...customInputs,
          [input.id]: booking.customInputs
            ? booking.customInputs[input.label as keyof typeof customInputType]
            : "",
        }),
        {}
      ),
      tosAgreement: false,
    };
  };

  const bookingFormSchema = z
    .object({
      name: z.string().min(1),
      email: z.string().trim().email(),
      guests: z.array(z.object({ email: z.string().email() })).optional(),
      phone: z
        .string()
        .refine((val) => isValidPhoneNumber(val))
        .optional()
        .nullable(),
      attendeeAddress: z.string().optional().nullable(),
      smsReminderNumber: z
        .string()
        .refine((val) => isValidPhoneNumber(val))
        .optional()
        .nullable(),
    })
    .passthrough();

  const bookingForm = useForm<BookingFormValues>({
    defaultValues: defaultValues(),
    resolver: zodResolver(bookingFormSchema), // Since this isn't set to strict we only validate the fields in the schema
  });
  const guestsField = useFieldArray({
    name: "guests",
    control: bookingForm.control,
  });

  const selectedLocationType = useWatch({
    control: bookingForm.control,
    name: "locationType",
    defaultValue: ((): EventLocationType["type"] | undefined => {
      if (router.query.location) {
        return router.query.location as EventLocationType["type"];
      }
      if (locations.length === 1) {
        return locations[0]?.type;
      }
    })(),
  });

  const selectedLocation = getEventLocationType(selectedLocationType);
  const AttendeeInput =
    selectedLocation?.attendeeInputType === "phone"
      ? PhoneInput
      : selectedLocation?.attendeeInputType === "attendeeAddress"
      ? AddressInput
      : null;

  // Calculate the booking date(s)
  let recurringStrings: string[] = [],
    recurringDates: Date[] = [];
  if (eventType.recurringEvent?.freq && recurringEventCount !== null) {
    [recurringStrings, recurringDates] = parseRecurringDates(
      {
        startDate: date,
        timeZone: timeZone(),
        recurringEvent: eventType.recurringEvent,
        recurringCount: parseInt(recurringEventCount.toString()),
      },
      i18n
    );
  }

  useEffect(() => {
    if (!loggedInIsOwner && !rescheduleUid && sessionStatus !== "loading" && session) {
      bookingForm.setValue("name", session.user.name ?? "");
      bookingForm.setValue("email", session.user.email ?? "");
    }
  }, [loggedInIsOwner, rescheduleUid, session, sessionStatus, bookingForm]);

  const userInfoError = bookingForm.formState.errors.name || bookingForm.formState.errors.email;

  const bookEvent = (booking: BookingFormValues) => {
    bookingForm.clearErrors();
    const bookingCustomInputs = Object.keys(booking.customInputs || {}).map((inputId) => ({
      label: eventType.customInputs.find((input) => input.id === parseInt(inputId))?.label || "",
      value: booking.customInputs && booking.customInputs[inputId] ? booking.customInputs[inputId] : "",
    }));

    // Checking if custom inputs of type Phone number are valid to display error message on UI
    if (eventType.customInputs.length) {
      let isErrorFound = false;
      eventType.customInputs.forEach((customInput) => {
        if (customInput.required && customInput.type === EventTypeCustomInputType.PHONE) {
          const input = bookingCustomInputs.find((i) => i.label === customInput.label);
          try {
            z.string({
              errorMap: () => ({
                message: `Missing ${customInput.type} customInput: '${customInput.label}'`,
              }),
            })
              .refine((val) => isValidPhoneNumber(val), {
                message: "Phone number is invalid",
              })
              .parse(input?.value);
          } catch (err) {
            isErrorFound = true;
            bookingForm.setError(`customInputs.${customInput.id}`, {
              type: "custom",
              message: "Invalid Phone number",
            });
          }
        }
      });
      if (isErrorFound) return;
    }

    telemetry.event(
      top !== window ? telemetryEventTypes.embedBookingConfirmed : telemetryEventTypes.bookingConfirmed,
      { isTeamBooking: document.URL.includes("team/") }
    );
    // "metadata" is a reserved key to allow for connecting external users without relying on the email address.
    // <...url>&metadata[user_id]=123 will be send as a custom input field as the hidden type.

    // @TODO: move to metadata
    const metadata = Object.keys(router.query)
      .filter((key) => key.startsWith("metadata"))
      .reduce(
        (metadata, key) => ({
          ...metadata,
          [key.substring("metadata[".length, key.length - 1)]: router.query[key],
        }),
        {}
      );

    if (eventType.customInputs.length > 0) {
      // find all required custom inputs and ensure they are filled out in the booking form
      const requiredCustomInputs = eventType.customInputs.filter((input) => input.required);
      const missingRequiredCustomInputs = requiredCustomInputs.filter(
        (input) => !booking?.customInputs?.[input.id]
      );
      if (missingRequiredCustomInputs.length > 0) {
        missingRequiredCustomInputs.forEach((input) => {
          bookingForm.setError(`customInputs.${input.id}`, {
            type: "required",
          });
        });
        return;
      }
    }

    // Validate that guests are unique
    let alreadyInvited = false;
    booking.guests?.forEach((guest, index) => {
      if (guest.email === booking.email) {
        bookingForm.setError(`guests.${index}`, { type: "validate", message: t("already_invited") });
        alreadyInvited = true;
      }

      if (booking.guests) {
        let guestCount = 0;
        for (const checkGuest of booking.guests) {
          if (checkGuest.email === guest.email) guestCount++;
          if (guestCount > 1) {
            bookingForm.setError(`guests.${index}`, { type: "validate", message: t("already_invited") });
            alreadyInvited = true;
            break;
          }
        }
      }
    });

    if (alreadyInvited) return;

    if (recurringDates.length) {
      // Identify set of bookings to one intance of recurring event to support batch changes
      const recurringEventId = uuidv4();
      const recurringBookings = recurringDates.map((recurringDate) => ({
        ...booking,
        tosAgreement: booking.tosAgreement,
        start: dayjs(recurringDate).format(),
        end: dayjs(recurringDate).add(duration, "minute").format(),
        eventTypeId: eventType.id,
        eventTypeSlug: eventType.slug,
        recurringEventId,
        // Added to track down the number of actual occurrences selected by the user
        recurringCount: recurringDates.length,
        timeZone: timeZone(),
        language: i18n.language,
        rescheduleUid,
        user: router.query.user,
        location: getEventLocationValue(locations, {
          type: booking.locationType ? booking.locationType : selectedLocationType || "",
          phone: booking.phone,
          attendeeAddress: booking.attendeeAddress,
        }),
        metadata,
        customInputs: bookingCustomInputs,
        hasHashedBookingLink,
        hashedLink,
        smsReminderNumber:
          selectedLocationType === LocationType.Phone
            ? booking.phone
            : booking.smsReminderNumber || undefined,
        ethSignature: gateState.rainbowToken,
        guests: booking.guests?.map((guest) => guest.email),
      }));
      recurringMutation.mutate(recurringBookings);
    } else {
      mutation.mutate({
        ...booking,
        tosAgreement: booking.tosAgreement,
        start: dayjs(date).format(),
        end: dayjs(date).add(duration, "minute").format(),
        eventTypeId: eventType.id,
        eventTypeSlug: eventType.slug,
        timeZone: timeZone(),
        language: i18n.language,
        rescheduleUid,
        bookingUid: router.query.bookingUid as string,
        user: router.query.user,
        location: getEventLocationValue(locations, {
          type: (booking.locationType ? booking.locationType : selectedLocationType) || "",
          phone: booking.phone,
          attendeeAddress: booking.attendeeAddress,
        }),
        metadata,
        customInputs: bookingCustomInputs,
        hasHashedBookingLink,
        hashedLink,
        smsReminderNumber:
          selectedLocationType === LocationType.Phone
            ? booking.phone
            : booking.smsReminderNumber || undefined,
        ethSignature: gateState.rainbowToken,
        guests: booking.guests?.map((guest) => guest.email),
      });
    }
  };

  // Should be disabled when rescheduleUid is present and data was found in defaultUserValues name/email fields.
  const disableInput = !!rescheduleUid && !!defaultUserValues.email && !!defaultUserValues.name;
  const disableLocations = !!rescheduleUid;
  const disabledExceptForOwner = disableInput && !loggedInIsOwner;
  const inputClassName = classNames(
    "dark:placeholder:text-darkgray-600 focus:border-brand dark:border-darkgray-300 dark:text-darkgray-900 block w-full rounded-md border-gray-300 focus:ring-black disabled:bg-gray-200 disabled:hover:cursor-not-allowed dark:bg-transparent dark:selection:bg-green-500 disabled:dark:text-gray-500",
    isEmbed ? "text-sm md:p-4 md:text-lg" : "text-sm"
  );

  let isSmsReminderNumberNeeded = false;
  let isSmsReminderNumberRequired = false;

  if (eventType.workflows.length > 0) {
    eventType.workflows.forEach((workflowReference) => {
      if (workflowReference.workflow.steps.length > 0) {
        workflowReference.workflow.steps.forEach((step) => {
          if (step.action === WorkflowActions.SMS_ATTENDEE) {
            isSmsReminderNumberNeeded = true;
            isSmsReminderNumberRequired = step.numberRequired || false;
            return;
          }
        });
      }
    });
  }
  const showEventTypeDetails = (isEmbed && !embedUiConfig.hideEventTypeDetails) || !isEmbed;
  const rainbowAppData = getEventTypeAppData(eventType, "rainbow") || {};

  // Define conditional gates here
  const gates = [
    // Rainbow gate is only added if the event has both a `blockchainId` and a `smartContractAddress`
    rainbowAppData && rainbowAppData.blockchainId && rainbowAppData.smartContractAddress
      ? ("rainbow" as Gate)
      : undefined,
  ];

  return (
    <Gates gates={gates} appData={rainbowAppData} dispatch={gateDispatcher}>
      <Head>
        <title>
          {rescheduleUid
            ? t("booking_reschedule_confirmation", {
                eventTypeTitle: eventType.title,
                profileName: profile.name,
              })
            : t("booking_confirmation", {
                eventTypeTitle: eventType.title,
                profileName: profile.name,
              })}{" "}
          | {APP_NAME}
        </title>
        <link rel="icon" href="/favico.ico" />
      </Head>
      <BookingPageTagManager eventType={eventType} />
      <CustomBranding lightVal={profile.brandColor} darkVal={profile.darkBrandColor} />
      <main
        className={classNames(
          !isEmbed && shouldAlignCentrally ? "mx-auto" : "",
          isEmbed ? "h-full w-full" : "max-w-3xl sm:my-24",
          "my-0"
        )}>
        <div
          className={classNames(
            "main overflow-hidden",
            isEmbed ? "h-full w-full" : "",
            isBackgroundTransparent ? "" : "dark:bg-darkgray-100 bg-white dark:border",
            "dark:border-darkgray-300 rounded-md"
          )}>
          <div className={classNames("sm:flex", isEmbed && "h-full w-full")}>
            {showEventTypeDetails && (
              <div
                className={classNames(
                  "sm:dark:border-darkgray-300 dark:text-darkgray-600 flex flex-col text-gray-600 sm:w-1/2 sm:border-r",
                  isEmbed ? "px-6 pb-0 pt-6 sm:pb-6 md:px-10 md:pb-10 md:pt-10" : "px-6 pb-0 pt-6 sm:pb-6"
                )}>
                <BookingDescription isBookingPage profile={profile} eventType={eventType} isEmbed={isEmbed}>
                  {paymentAppData.price > 0 && (
                    <p
                      className={classNames(
                        "text-bookinglight -ml-2 px-2",
                        isEmbed ? "text-sm md:text-lg" : "text-sm"
                      )}>
                      <FiCreditCard
                        className={classNames(
                          "-mt-1 ml-[2px] inline-block ltr:mr-[10px] rtl:ml-[10px]",
                          isEmbed ? "h-4 w-4 md:h-6 md:w-6" : "h-4 w-4"
                        )}
                      />
                      <IntlProvider locale="en">
                        <FormattedNumber
                          value={paymentAppData.price / 100.0}
                          style="currency"
                          currency={paymentAppData?.currency?.toUpperCase()}
                        />
                      </IntlProvider>
                    </p>
                  )}
                  {!rescheduleUid && eventType.recurringEvent?.freq && recurringEventCount && (
                    <div
                      className={classNames(
                        "items-start font-medium text-gray-600 dark:text-white",
                        isEmbed ? "text-sm md:text-lg" : "text-sm"
                      )}>
                      <FiRefreshCw
                        className={classNames(
                          "ml-[2px] inline-block ltr:mr-[10px] rtl:ml-[10px]",
                          isEmbed ? "h-4 w-4 md:h-6 md:w-6" : "h-4 w-4"
                        )}
                      />
                      <p className="-ml-2 inline-block items-center px-2">
                        {getEveryFreqFor({
                          t,
                          recurringEvent: eventType.recurringEvent,
                          recurringCount: recurringEventCount,
                        })}
                      </p>
                    </div>
                  )}
                  <div className="text-bookinghighlight flex items-start text-sm">
                    <FiCalendar className="ml-[2px] mt-[2px] inline-block h-4 w-4 ltr:mr-[10px] rtl:ml-[10px]" />
                    <div className="text-sm font-medium">
                      {(rescheduleUid || !eventType.recurringEvent?.freq) && `${parseDate(date, i18n)}`}
                      {!rescheduleUid &&
                        eventType.recurringEvent?.freq &&
                        recurringStrings.slice(0, 5).map((timeFormatted, key) => {
                          return <p key={key}>{timeFormatted}</p>;
                        })}
                      {!rescheduleUid && eventType.recurringEvent?.freq && recurringStrings.length > 5 && (
                        <div className="flex">
                          <Tooltip
                            content={recurringStrings.slice(5).map((timeFormatted, key) => (
                              <p key={key}>{timeFormatted}</p>
                            ))}>
                            <p className="dark:text-darkgray-600 text-sm">
                              + {t("plus_more", { count: recurringStrings.length - 5 })}
                            </p>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                  {booking?.startTime && rescheduleUid && (
                    <div>
                      <p className="mb-2 mt-8 text-sm" data-testid="former_time_p">
                        {t("former_time")}
                      </p>
                      <p className="line-through">
                        <FiCalendar className="-mt-1 ml-[2px] inline-block h-4 w-4 ltr:mr-[10px] rtl:ml-[10px]" />
                        {typeof booking.startTime === "string" && parseDate(dayjs(booking.startTime), i18n)}
                      </p>
                    </div>
                  )}
                  {!!eventType.seatsPerTimeSlot && (
                    <div className="text-bookinghighlight flex items-start text-sm">
                      <FiUser
                        className={`ml-[2px] mt-[2px] inline-block h-4 w-4 ltr:mr-[10px] rtl:ml-[10px] ${
                          booking && booking.attendees.length / eventType.seatsPerTimeSlot >= 0.5
                            ? "text-rose-600"
                            : booking && booking.attendees.length / eventType.seatsPerTimeSlot >= 0.33
                            ? "text-yellow-500"
                            : "text-bookinghighlight"
                        }`}
                      />
                      <p
                        className={`${
                          booking && booking.attendees.length / eventType.seatsPerTimeSlot >= 0.5
                            ? "text-rose-600"
                            : booking && booking.attendees.length / eventType.seatsPerTimeSlot >= 0.33
                            ? "text-yellow-500"
                            : "text-bookinghighlight"
                        } mb-2 font-medium`}>
                        {booking
                          ? eventType.seatsPerTimeSlot - booking.attendees.length
                          : eventType.seatsPerTimeSlot}{" "}
                        / {eventType.seatsPerTimeSlot} {t("seats_available")}
                      </p>
                    </div>
                  )}
                </BookingDescription>
              </div>
            )}
            <div
              className={classNames(
                showEventTypeDetails ? "sm:w-1/2" : "w-full",
                isEmbed ? "p-6 md:p-10" : "p-6"
              )}>
              <Form form={bookingForm} handleSubmit={bookEvent}>
                {sessionStatus === "loading" ? (
                  <div className="mb-4 h-24 animate-pulse rounded-md bg-gray-200" />
                ) : null}

                {sessionStatus !== "loading" && !session ? (
                  <div
                    className={classNames(
                      "flex flex-col items-center gap-2 rounded-md border border-gray-100 bg-gray-100",
                      isEmbed ? "mb-4 p-4 text-base md:mb-6 md:p-6 md:text-lg" : "mb-4 p-4 text-base",
                      !!userInfoError && "border-red-700 ring-4 ring-red-700/20"
                    )}>
                    {t("lb_book_account_required")}
                    <div className="flex gap-x-2">
                      <Button
                        type="button"
                        color="primary"
                        className={isEmbed ? "md:px-6 md:py-3 md:text-lg" : ""}
                        onClick={() => {
                          router.replace({
                            pathname: "/signup/client",
                            query: {
                              callbackUrl: `${location.pathname}${location.search}`,
                            },
                          });
                        }}>
                        {t("create_account")}
                      </Button>
                      <Button
                        type="button"
                        color="minimal"
                        className={classNames(
                          "hover:bg-gray-200",
                          isEmbed ? "md:px-6 md:py-3 md:text-lg" : ""
                        )}
                        onClick={() => {
                          router.replace({
                            pathname: "/auth/login",
                            query: {
                              callbackUrl: `${WEBAPP_URL}${location.pathname}${location.search}`,
                            },
                          });
                        }}>
                        {t("login")}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {disableInput || (sessionStatus !== "loading" && !loggedInIsOwner && session) ? (
                  <>
                    <div
                      className={classNames(
                        "font-medium text-gray-700 dark:text-white",
                        isEmbed ? "text-sm md:text-lg" : "text-sm"
                      )}>
                      {t("your_name")}
                    </div>
                    <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                      <Controller
                        control={bookingForm.control}
                        name="name"
                        render={({ field: { value } }) => <>{value}</>}
                      />
                    </div>
                    <div
                      className={classNames(
                        "font-medium text-gray-700 dark:text-white",
                        isEmbed ? "text-sm md:text-lg" : "text-sm"
                      )}>
                      {t("email_address")}
                    </div>
                    <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                      <Controller
                        control={bookingForm.control}
                        name="email"
                        render={({ field: { value } }) => <>{value}</>}
                      />
                    </div>
                  </>
                ) : null}

                {loggedInIsOwner ? (
                  <>
                    <div
                      className={classNames(
                        "rounded-md border border-orange-500 bg-orange-100 px-3 py-2 text-black/90",
                        isEmbed ? "mb-4 text-sm md:mb-6 md:text-lg" : "mb-4 text-sm"
                      )}>
                      {t("lb_book_own_account")}
                    </div>
                    <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                      <label
                        htmlFor="name"
                        className={classNames(
                          "block font-medium text-gray-700 dark:text-white",
                          isEmbed ? "text-sm md:mb-2 md:text-lg" : "text-sm"
                        )}>
                        {t("your_name")}
                      </label>
                      <div className="mt-1">
                        <input
                          {...bookingForm.register("name", { required: true })}
                          type="text"
                          name="name"
                          id="name"
                          required
                          className={inputClassName}
                          placeholder={t("example_name")}
                          disabled={disableInput}
                        />
                      </div>
                    </div>
                    <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                      <label
                        htmlFor="email"
                        className={classNames(
                          "block font-medium text-gray-700 dark:text-white",
                          isEmbed ? "text-sm md:mb-2 md:text-lg" : "text-sm"
                        )}>
                        {t("email_address")}
                      </label>
                      <div className="mt-1">
                        <EmailInput
                          {...bookingForm.register("email")}
                          required
                          className={classNames(
                            inputClassName,
                            bookingForm.formState.errors.email && "!focus:ring-red-700 !border-red-700"
                          )}
                          placeholder="you@example.com"
                          type="search" // Disables annoying 1password intrusive popup (non-optimal, I know I know...)
                          disabled={disableInput}
                        />
                        {bookingForm.formState.errors.email && (
                          <div
                            className={classNames(
                              "mt-2 flex items-center text-red-700",
                              isEmbed ? "text-sm md:text-lg" : "text-sm"
                            )}>
                            <FiInfo
                              className={classNames(
                                "ltr:mr-2 rtl:ml-2",
                                isEmbed ? "h-3 w-3 md:h-5 md:w-5" : "h-3 w-3"
                              )}
                            />
                            <p>{t("email_validation_error")}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}

                <>
                  {rescheduleUid ? (
                    <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                      <span
                        className={classNames(
                          "block font-medium text-gray-700 dark:text-white",
                          isEmbed ? "text-sm md:text-lg" : "text-sm"
                        )}>
                        {t("location")}
                      </span>
                      <p
                        className={classNames(
                          "mt-1 text-gray-500",
                          isEmbed ? "text-sm md:text-lg" : "text-sm"
                        )}>
                        {getHumanReadableLocationValue(booking?.location, t)}
                      </p>
                    </div>
                  ) : (
                    locations.length > 1 && (
                      <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                        <span
                          className={classNames(
                            "block font-medium text-gray-700 dark:text-white",
                            isEmbed ? "text-sm md:text-lg" : "text-sm"
                          )}>
                          {t("location")}
                        </span>
                        {locations.map((location, i) => {
                          const locationString = locationKeyToString(location);
                          if (!selectedLocationType) {
                            bookingForm.setValue("locationType", locations[0].type);
                          }
                          if (typeof locationString !== "string") {
                            // It's possible that location app got uninstalled
                            return null;
                          }
                          return (
                            <label
                              key={i}
                              className={classNames("block", isEmbed ? "text-sm md:text-lg" : "text-sm")}>
                              <input
                                type="radio"
                                disabled={!!disableLocations}
                                className={classNames(
                                  "location dark:bg-darkgray-300 dark:border-darkgray-300 border-gray-300 text-black focus:ring-black ltr:mr-2 rtl:ml-2",
                                  isEmbed ? "h-4 w-4 md:h-6 md:w-6" : "h-4 w-4"
                                )}
                                {...bookingForm.register("locationType", { required: true })}
                                value={location.type}
                                defaultChecked={i === 0}
                              />
                              <span className="ltr:ml-2 ltr:mr-2 rtl:ml-2 dark:text-white">
                                {t(locationKeyToString(location) ?? "")}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )
                  )}
                </>
                {/* TODO: Change name and id ="phone" to something generic */}
                {AttendeeInput && !disableInput && (
                  <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                    <label
                      htmlFor={
                        selectedLocationType === LocationType.Phone
                          ? "phone"
                          : selectedLocationType === LocationType.AttendeeInPerson
                          ? "attendeeAddress"
                          : ""
                      }
                      className={classNames(
                        "block font-medium text-gray-700 dark:text-white",
                        isEmbed ? "text-sm md:mb-2 md:text-lg" : "text-sm"
                      )}>
                      {selectedLocationType === LocationType.Phone
                        ? t("phone_number")
                        : selectedLocationType === LocationType.AttendeeInPerson
                        ? t("address")
                        : ""}
                    </label>
                    <div className="mt-1">
                      <AttendeeInput<BookingFormValues>
                        control={bookingForm.control}
                        bookingForm={bookingForm}
                        name={
                          selectedLocationType === LocationType.Phone
                            ? "phone"
                            : selectedLocationType === LocationType.AttendeeInPerson
                            ? "attendeeAddress"
                            : ""
                        }
                        placeholder={t(selectedLocation?.attendeeInputPlaceholder || "")}
                        id={
                          selectedLocationType === LocationType.Phone
                            ? "phone"
                            : selectedLocationType === LocationType.AttendeeInPerson
                            ? "attendeeAddress"
                            : ""
                        }
                        required
                      />
                    </div>
                    {bookingForm.formState.errors.phone && (
                      <div
                        className={classNames(
                          "mt-2 flex items-center text-red-700",
                          isEmbed ? "text-sm md:text-lg" : "text-sm"
                        )}>
                        <FiInfo
                          className={classNames(
                            "ltr:mr-2 rtl:ml-2",
                            isEmbed ? "h-3 w-3 md:h-5 md:w-5" : "h-3 w-3"
                          )}
                        />
                        <p>{t("invalid_number")}</p>
                      </div>
                    )}
                  </div>
                )}
                {eventType.customInputs
                  .sort((a, b) => a.id - b.id)
                  .map((input) => (
                    <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"} key={input.id}>
                      {input.type !== EventTypeCustomInputType.BOOL && (
                        <label
                          htmlFor={"custom_" + input.id}
                          className={classNames(
                            "mb-1 block font-medium text-gray-700 transition-colors dark:text-white",
                            isEmbed ? "text-sm md:text-lg" : "text-sm",
                            bookingForm.formState.errors.customInputs?.[input.id] && "!text-red-700"
                          )}>
                          {input.label} {input.required && <span className="text-red-700">*</span>}
                        </label>
                      )}
                      {input.type === EventTypeCustomInputType.TEXTLONG && (
                        <textarea
                          {...bookingForm.register(`customInputs.${input.id}`, {
                            required: input.required,
                          })}
                          required={input.required}
                          id={"custom_" + input.id}
                          rows={3}
                          className={inputClassName}
                          placeholder={input.placeholder}
                          disabled={disabledExceptForOwner}
                        />
                      )}
                      {input.type === EventTypeCustomInputType.TEXT && (
                        <input
                          type="text"
                          {...bookingForm.register(`customInputs.${input.id}`, {
                            required: input.required,
                          })}
                          required={input.required}
                          id={"custom_" + input.id}
                          className={inputClassName}
                          placeholder={input.placeholder}
                          disabled={disabledExceptForOwner}
                        />
                      )}
                      {input.type === EventTypeCustomInputType.NUMBER && (
                        <input
                          type="number"
                          {...bookingForm.register(`customInputs.${input.id}`, {
                            required: input.required,
                          })}
                          required={input.required}
                          id={"custom_" + input.id}
                          className={inputClassName}
                          placeholder=""
                          disabled={disabledExceptForOwner}
                        />
                      )}
                      {input.type === EventTypeCustomInputType.BOOL && (
                        <div className="my-6">
                          <div className="flex">
                            <input
                              type="checkbox"
                              {...bookingForm.register(`customInputs.${input.id}`, {
                                required: input.required,
                              })}
                              required={input.required}
                              id={"custom_" + input.id}
                              className={classNames(
                                "rounded border-gray-300 text-black focus:ring-black disabled:bg-gray-200 ltr:mr-2 rtl:ml-2 disabled:dark:text-gray-500",
                                isEmbed ? "h-4 w-4 md:h-6 md:w-6" : "h-4 w-4"
                              )}
                              placeholder=""
                              disabled={disabledExceptForOwner}
                            />
                            <label
                              htmlFor={"custom_" + input.id}
                              className={classNames(
                                "-mt-px block font-medium text-gray-700 dark:text-white",
                                isEmbed ? "text-sm md:text-lg" : "text-sm"
                              )}>
                              {input.label}
                            </label>
                          </div>
                        </div>
                      )}
                      {input.options && input.type === EventTypeCustomInputType.RADIO && (
                        <div className="flex">
                          <Group
                            name={`customInputs.${input.id}`}
                            required={input.required}
                            onValueChange={(e) => {
                              bookingForm.setValue(`customInputs.${input.id}`, e);
                            }}>
                            <>
                              {input.options.map((option, i) => (
                                <RadioField
                                  label={option.label}
                                  key={`option.${input.id}.${i}.radio`}
                                  value={option.label}
                                  id={`option.${input.id}.${i}.radio`}
                                />
                              ))}
                            </>
                            {bookingForm.formState.errors.customInputs?.[input.id] && (
                              <div
                                className={classNames(
                                  "mt-px flex items-center text-red-700",
                                  isEmbed ? "text-xs md:text-base" : "text-xs"
                                )}>
                                <p>{t("required")}</p>
                              </div>
                            )}
                          </Group>
                        </div>
                      )}
                      {input.type === EventTypeCustomInputType.PHONE && (
                        <div>
                          <PhoneInput<BookingFormValues>
                            name={`customInputs.${input.id}`}
                            control={bookingForm.control}
                            placeholder={t("enter_phone_number")}
                            id={`customInputs.${input.id}`}
                            required={input.required}
                          />
                          {bookingForm.formState.errors?.customInputs?.[input.id] && (
                            <div
                              className={classNames(
                                "mt-2 flex items-center text-red-700",
                                isEmbed ? "text-sm md:text-lg" : "text-sm"
                              )}>
                              <FiInfo
                                className={classNames(
                                  "ltr:mr-2 rtl:ml-2",
                                  isEmbed ? "h-3 w-3 md:h-5 md:w-5" : "h-3 w-3"
                                )}
                              />
                              <p>{t("invalid_number")}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                {isSmsReminderNumberNeeded && selectedLocationType !== LocationType.Phone && (
                  <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                    <label
                      htmlFor="smsReminderNumber"
                      className={classNames(
                        "block font-medium text-gray-700 dark:text-white",
                        isEmbed ? "text-sm md:mb-2 md:text-lg" : "mb-1 text-sm"
                      )}>
                      {t("number_sms_notifications")}
                    </label>
                    <div className="mt-1">
                      <PhoneInput<BookingFormValues>
                        control={bookingForm.control}
                        name="smsReminderNumber"
                        placeholder={t("enter_phone_number")}
                        id="smsReminderNumber"
                        required={isSmsReminderNumberRequired}
                      />
                    </div>
                    {bookingForm.formState.errors.smsReminderNumber && (
                      <div
                        className={classNames(
                          "mt-2 flex items-center text-red-700",
                          isEmbed ? "text-sm md:text-lg" : "text-sm"
                        )}>
                        <FiInfo
                          className={classNames(
                            "ltr:mr-2 rtl:ml-2",
                            isEmbed ? "h-3 w-3 md:h-5 md:w-5" : "h-3 w-3"
                          )}
                        />
                        <p>{t("invalid_number")}</p>
                      </div>
                    )}
                  </div>
                )}
                <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                  <label
                    htmlFor="notes"
                    className={classNames(
                      "block font-medium text-gray-700 dark:text-white",
                      isEmbed ? "text-sm md:mb-2 md:text-lg" : "mb-1 text-sm"
                    )}>
                    {rescheduleUid ? t("reschedule_optional") : t("additional_notes")}
                  </label>
                  {rescheduleUid ? (
                    <textarea
                      {...bookingForm.register("rescheduleReason")}
                      id="rescheduleReason"
                      name="rescheduleReason"
                      rows={isEmbed ? 5 : 3}
                      className={inputClassName}
                      placeholder={t("reschedule_placeholder")}
                    />
                  ) : (
                    <textarea
                      {...bookingForm.register("notes")}
                      required={!!eventType.metadata?.additionalNotesRequired}
                      id="notes"
                      name="notes"
                      rows={isEmbed ? 5 : 3}
                      className={inputClassName}
                      placeholder={t("share_additional_notes")}
                      disabled={disabledExceptForOwner}
                    />
                  )}
                </div>
                {!eventType.disableGuests && guestsField.fields.length ? (
                  <div className={isEmbed ? "mb-4 md:mb-6" : "mb-4"}>
                    <div>
                      <label
                        htmlFor="guests"
                        className={classNames(
                          "block font-medium text-gray-700 dark:text-white",
                          isEmbed ? "text-sm md:mb-2 md:text-lg" : "mb-1 text-sm"
                        )}>
                        {t("guests")}
                      </label>
                      <ul>
                        {guestsField.fields.map((field, index) => (
                          <li key={field.id}>
                            <EmailField
                              {...bookingForm.register(`guests.${index}.email` as const)}
                              className={classNames(
                                inputClassName,
                                bookingForm.formState.errors.guests?.[index] &&
                                  "!focus:ring-red-700 !border-red-700",
                                "border-r-0"
                              )}
                              addOnClassname={classNames(
                                "border-gray-300 border block border-l-0 disabled:bg-gray-200 disabled:hover:cursor-not-allowed bg-transparent disabled:text-gray-500 dark:border-darkgray-300 ",
                                bookingForm.formState.errors.guests?.[index] &&
                                  "!focus:ring-red-700 !border-red-700"
                              )}
                              placeholder="guest@example.com"
                              label={<></>}
                              required
                              addOnSuffix={
                                <Tooltip content="Remove guest">
                                  <button
                                    className="m-1 disabled:hover:cursor-not-allowed"
                                    type="button"
                                    onClick={() => guestsField.remove(index)}>
                                    <FiX className="text-gray-600" />
                                  </button>
                                </Tooltip>
                              }
                            />
                            {bookingForm.formState.errors.guests?.[index] && (
                              <div
                                className={classNames(
                                  "mt-2 flex items-center text-red-700",
                                  isEmbed ? "text-sm md:text-lg" : "text-sm"
                                )}>
                                <FiInfo
                                  className={classNames(
                                    "ltr:mr-2 rtl:ml-2",
                                    isEmbed ? "h-3 w-3 md:h-5 md:w-5" : "h-3 w-3"
                                  )}
                                />
                                <p className="text-red-700">
                                  {bookingForm.formState.errors.guests?.[index]?.message}
                                </p>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                      <Button
                        type="button"
                        color="minimal"
                        StartIcon={FiUserPlus}
                        className={classNames("my-2.5", isEmbed ? "md:text-lg" : "")}
                        onClick={() => guestsField.append({ email: "" })}>
                        {t("add_another")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <></>
                )}

                {!eventType.disableGuests && !guestsField.fields.length && (
                  <Button
                    color="minimal"
                    variant="button"
                    StartIcon={FiUserPlus}
                    onClick={() => {
                      guestsField.append({ email: "" });
                    }}
                    className={classNames("mr-auto", isEmbed ? "md:text-lg" : "")}>
                    {t("additional_guests")}
                  </Button>
                )}

                {profile.tosUrl ? (
                  <div className={isEmbed ? "mb-4 mt-4 md:mb-6 md:mt-6" : "mb-4 mt-4"}>
                    <Checkbox
                      {...bookingForm.register("tosAgreement", { required: true })}
                      required
                      description={
                        <Trans t={t} i18nKey="coach_tos_agreement">
                          I have read and agree to the{" "}
                          <a
                            href={profile.tosUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-700 underline underline-offset-2">
                            General Terms and Conditions
                          </a>{" "}
                          of this coach
                        </Trans>
                      }
                    />
                  </div>
                ) : null}

                <div
                  className={classNames(
                    "flex justify-end rtl:space-x-reverse",
                    isEmbed ? "space-x-2 md:space-x-4" : "space-x-2"
                  )}>
                  <Button
                    color="minimal"
                    type="button"
                    className={isEmbed ? "md:px-6 md:py-3 md:text-lg" : ""}
                    onClick={() => {
                      if (handleCancel) {
                        handleCancel();
                        return;
                      }
                      router.back();
                    }}>
                    {t("cancel")}
                  </Button>
                  <Button
                    type="submit"
                    data-testid={rescheduleUid ? "confirm-reschedule-button" : "confirm-book-button"}
                    loading={mutation.isLoading || recurringMutation.isLoading}
                    className={isEmbed ? "md:px-8 md:py-4 md:text-xl" : ""}>
                    {rescheduleUid ? t("reschedule") : t("confirm")}
                  </Button>
                </div>
              </Form>
              {(mutation.isError || recurringMutation.isError) && (
                <ErrorMessage error={mutation.error || recurringMutation.error} />
              )}
            </div>
          </div>
        </div>
      </main>
    </Gates>
  );
};

export default BookingPage;

function ErrorMessage({ error }: { error: unknown }) {
  const { t } = useLocale();
  const { query: { rescheduleUid } = {} } = useRouter();

  return (
    <div data-testid="booking-fail" className="mt-2 border-l-4 border-yellow-400 bg-yellow-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <FiAlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
        </div>
        <div className="ltr:ml-3 rtl:mr-3">
          <p className="text-sm text-yellow-700">
            {rescheduleUid ? t("reschedule_fail") : t("booking_fail")}{" "}
            {error instanceof HttpError || error instanceof Error ? t(error.message) : "Unknown error"}
          </p>
        </div>
      </div>
    </div>
  );
}
